import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TicketModal from "../../components/TicketModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { getMessagePreview as buildMessagePreview } from "../../utils/messagePreview";

const PENDING_STATUSES = [
  "aguardando",
  "em_analise",
  "em_producao",
  "retornar_cliente",
  "finalizado"
];

const STATUS_COLORS = {
  aguardando: "#ff9800",
  em_analise: "#2196f3",
  em_producao: "#00acc1",
  retornar_cliente: "#9c27b0",
  finalizado: "#4caf50",
  atrasado: "#e53935"
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles
  },
  filtersRow: {
    marginBottom: theme.spacing(1)
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto"
  },
  table: {
    width: "100%",
    tableLayout: "fixed"
  },
  narrowCell: {
    width: 190,
    maxWidth: 190,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  statusCell: {
    width: 170,
    maxWidth: 170
  },
  paginationWrapper: {
    borderTop: "1px solid rgba(224, 224, 224, 1)"
  },
  messageCell: {
    width: "auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  actionsCell: {
    display: "flex",
    justifyContent: "center",
    gap: 8
  },
  actionButton: {
    padding: 6
  },
  deleteButton: {
    padding: 6,
    color: "#e53935"
  },
  statusChip: {
    cursor: "pointer",
    fontSize: "0.7rem",
    height: 24,
    maxWidth: "100%"
  },
  statusButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  statusButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    borderRadius: 18,
    border: "1.5px solid",
    background: "transparent",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1.4,
    outline: "none",
    transition: "all 0.15s ease"
  },
  statusButtonCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 10,
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#fff"
  }
}));

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

// getMessagePreview/getMediaPrefix vivem em utils/messagePreview desde
// 29/07/2026 (o Relatorio de Mensagens usa a mesma previa). Aqui os rotulos
// continuam vindo do i18n desta tela.
const getMessagePreview = (message) =>
  buildMessagePreview(message, {
    deleted: i18n.t("pendingMessages.table.messageDeleted"),
    fileTypePrefix: i18n.t("pendingMessages.table.fileTypePrefix"),
    withoutContent: i18n.t("pendingMessages.table.messageWithoutContent")
  });

const StatusChip = ({ messageId, currentStatus, onStatusChange, disabled }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const color = STATUS_COLORS[currentStatus] || "#9e9e9e";
  const label = currentStatus
    ? i18n.t(`pendingMessages.statuses.${currentStatus}`)
    : i18n.t("pendingMessages.statuses.none");

  const handleOpen = (e) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (status) => {
    handleClose();
    if (status !== currentStatus) {
      onStatusChange(messageId, status);
    }
  };

  return (
    <>
      <Chip
        label={label}
        size="small"
        className={classes.statusChip}
        style={{ backgroundColor: color, color: "#fff" }}
        onClick={handleOpen}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {PENDING_STATUSES.map((s) => (
          <MenuItem key={s} selected={s === currentStatus} onClick={() => handleSelect(s)}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: STATUS_COLORS[s],
                marginRight: 8,
                flexShrink: 0
              }}
            />
            {i18n.t(`pendingMessages.statuses.${s}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const PendingMessages = () => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [count, setCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [searchParam, setSearchParam] = useState("");
  const [contact, setContact] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pendingUserId, setPendingUserId] = useState("");
  const [pendingStatusFilter, setPendingStatusFilter] = useState("");

  const [users, setUsers] = useState([]);
  const [removingMessageIds, setRemovingMessageIds] = useState(new Set());
  const [updatingStatusIds, setUpdatingStatusIds] = useState(new Set());
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketUuid, setTicketUuid] = useState(null);
  const [jumpToMessageId, setJumpToMessageId] = useState(null);
  const [jumpToMessageTs, setJumpToMessageTs] = useState(null);

  const userNameById = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      map.set(String(user.id), user.name);
    });
    return map;
  }, [users]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const { data: usersData } = await api.get("/users/list");

      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchPendingMessages = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        pageNumber: pageNumber + 1,
        pageSize: rowsPerPage
      };

      if (searchParam.trim()) params.searchParam = searchParam.trim();
      if (contact.trim()) {
        params.contact = contact.trim();
        if (/^\d+$/.test(contact.trim())) {
          params.contactId = contact.trim();
        }
      }
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (pendingUserId) params.pendingUserId = pendingUserId;
      if (pendingStatusFilter) params.pendingStatus = pendingStatusFilter;

      const { data } = await api.get("/messages/pending", { params });
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      setCount(Number(data?.count || 0));
      setStatusCounts(
        data?.statusCounts && typeof data.statusCounts === "object"
          ? data.statusCounts
          : {}
      );
    } catch (err) {
      toastError(err);
      setMessages([]);
      setCount(0);
      setStatusCounts({});
    } finally {
      setLoading(false);
    }
  }, [pageNumber, rowsPerPage, searchParam, contact, dateFrom, dateTo, pendingUserId, pendingStatusFilter]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchPendingMessages();
    }, 400);

    return () => clearTimeout(debounce);
  }, [fetchPendingMessages]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;

    const socket = socketManager.getSocket(companyId);

    const handleRealtimeMessageUpdate = (data) => {
      if (!data || data.action !== "update") return;

      const updatedMessage = data?.message;
      const updatedMessageId = String(updatedMessage?.id || "");
      if (!updatedMessageId) return;

      if (updatedMessage?.isPending === true) {
        setMessages((prevMessages) => {
          const existsInList = prevMessages.some((item) => String(item?.id) === updatedMessageId);
          if (!existsInList) {
            fetchPendingMessages();
          }
          return prevMessages;
        });
      }

      setMessages((prevMessages) => {
        const existsInList = prevMessages.some((item) => String(item?.id) === updatedMessageId);
        if (!existsInList) {
          return prevMessages;
        }

        if (updatedMessage?.isPending === false) {
          setCount((prevCount) => Math.max(0, Number(prevCount || 0) - 1));
          return prevMessages.filter((item) => String(item?.id) !== updatedMessageId);
        }

        return prevMessages.map((item) =>
          String(item?.id) === updatedMessageId
            ? {
                ...item,
                ...updatedMessage
              }
            : item
        );
      });
    };

    socket.on(`company-${companyId}-appMessage`, handleRealtimeMessageUpdate);

    return () => {
      socket.off(`company-${companyId}-appMessage`, handleRealtimeMessageUpdate);
    };
  }, [socketManager, fetchPendingMessages]);

  const handleOpenTicketMessages = async (message) => {
    setJumpToMessageId(String(message?.id || ""));
    setJumpToMessageTs(Date.now());

    const currentTicketUuid = message?.ticket?.uuid;
    if (currentTicketUuid) {
      setTicketUuid(currentTicketUuid);
      setTicketModalOpen(true);
      return;
    }

    const ticketId = message?.ticket?.id;
    if (!ticketId) {
      toastError({ message: i18n.t("pendingMessages.toasts.ticketNotFound") });
      return;
    }

    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      if (!data?.uuid) {
        toastError({
          message: i18n.t("pendingMessages.toasts.loadConversationFailed")
        });
        return;
      }

      setTicketUuid(data.uuid);
      setTicketModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
    setTicketUuid(null);
    setJumpToMessageId(null);
    setJumpToMessageTs(null);
  };

  const handleRemovePendingMessage = async (messageId) => {
    const normalizedMessageId = String(messageId || "");
    if (!normalizedMessageId) return;

    const alreadyRemoving = removingMessageIds.has(normalizedMessageId);
    if (alreadyRemoving) return;

    setRemovingMessageIds((prevIds) => {
      const nextIds = new Set(prevIds);
      nextIds.add(normalizedMessageId);
      return nextIds;
    });

    try {
      await api.post(`/messages/${normalizedMessageId}/pending`);

      setMessages((prevMessages) =>
        prevMessages.filter((item) => String(item?.id || "") !== normalizedMessageId)
      );
      setCount((prevCount) => Math.max(0, Number(prevCount || 0) - 1));

      toast.success(i18n.t("pendingMessages.toasts.removed"));
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingMessageIds((prevIds) => {
        const nextIds = new Set(prevIds);
        nextIds.delete(normalizedMessageId);
        return nextIds;
      });
    }
  };

  const handleUpdatePendingStatus = async (messageId, newStatus) => {
    const normalizedMessageId = String(messageId || "");
    if (!normalizedMessageId) return;

    setUpdatingStatusIds((prev) => {
      const next = new Set(prev);
      next.add(normalizedMessageId);
      return next;
    });

    setMessages((prevMessages) =>
      prevMessages.map((item) =>
        String(item?.id) === normalizedMessageId
          ? { ...item, pendingStatus: newStatus }
          : item
      )
    );

    try {
      await api.patch(`/messages/${normalizedMessageId}/pending-status`, { pendingStatus: newStatus });
      toast.success(i18n.t("pendingMessages.toasts.statusUpdated"));
    } catch (err) {
      toastError(err);
      fetchPendingMessages();
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(normalizedMessageId);
        return next;
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  const resetPageAndSet = (setter) => (event) => {
    setter(event.target.value);
    setPageNumber(0);
  };

  const handleStatusButtonClick = (status) => {
    setPendingStatusFilter((prev) => (prev === status ? "" : status));
    setPageNumber(0);
  };

  // Total de "Todos": soma das contagens por status. Exclui o virtual "atrasado"
  // (subconjunto transversal aos demais) e "finalizado", que a listagem esconde.
  const totalStatusCount = Object.entries(statusCounts).reduce(
    (acc, [key, value]) =>
      key === "atrasado" || key === "finalizado" ? acc : acc + (Number(value) || 0),
    0
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("pendingMessages.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={1} className={classes.filtersRow}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="search"
              label={i18n.t("pendingMessages.filters.search")}
              value={searchParam}
              onChange={resetPageAndSet(setSearchParam)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label={i18n.t("pendingMessages.filters.contact")}
              value={contact}
              onChange={resetPageAndSet(setContact)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label={i18n.t("pendingMessages.filters.dateFrom")}
              value={dateFrom}
              onChange={resetPageAndSet(setDateFrom)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label={i18n.t("pendingMessages.filters.dateTo")}
              value={dateTo}
              onChange={resetPageAndSet(setDateTo)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label={i18n.t("pendingMessages.filters.pendingUser")}
              value={pendingUserId}
              onChange={resetPageAndSet(setPendingUserId)}
            >
              <MenuItem value="">{i18n.t("pendingMessages.filters.all")}</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={String(user.id)}>{user.name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={1}>
            <TextField
              fullWidth
              select
              label={i18n.t("pendingMessages.filters.status")}
              value={pendingStatusFilter}
              onChange={resetPageAndSet(setPendingStatusFilter)}
            >
              <MenuItem value="">{i18n.t("pendingMessages.filters.all")}</MenuItem>
              {[...PENDING_STATUSES, "atrasado"].map((s) => (
                <MenuItem key={s} value={s}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: STATUS_COLORS[s],
                      marginRight: 8,
                      flexShrink: 0
                    }}
                  />
                  {i18n.t(`pendingMessages.statuses.${s}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

        </Grid>

        <div className={classes.statusButtonsRow}>
          {(() => {
            const active = !pendingStatusFilter;
            const color = "#607d8b";
            return (
              <button
                key="todos"
                type="button"
                className={classes.statusButton}
                style={{
                  borderColor: color,
                  color: active ? "#fff" : color,
                  backgroundColor: active ? color : "transparent"
                }}
                onClick={() => handleStatusButtonClick("")}
              >
                <span>{i18n.t("pendingMessages.filters.all")}</span>
                <span
                  className={classes.statusButtonCount}
                  style={{
                    backgroundColor: active ? "rgba(255,255,255,0.35)" : color
                  }}
                >
                  {totalStatusCount}
                </span>
              </button>
            );
          })()}
          {[...PENDING_STATUSES, "atrasado"].map((s) => {
            const color = STATUS_COLORS[s] || "#9e9e9e";
            const active = pendingStatusFilter === s;
            return (
              <button
                key={s}
                type="button"
                className={classes.statusButton}
                style={{
                  borderColor: color,
                  color: active ? "#fff" : color,
                  backgroundColor: active ? color : "transparent"
                }}
                onClick={() => handleStatusButtonClick(s)}
              >
                <span>{i18n.t(`pendingMessages.statuses.${s}`)}</span>
                <span
                  className={classes.statusButtonCount}
                  style={{
                    backgroundColor: active ? "rgba(255,255,255,0.35)" : color
                  }}
                >
                  {statusCounts[s] || 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" className={classes.table}>
            <colgroup>
              <col style={{ width: "190px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "170px" }} />
              <col />
              <col style={{ width: "190px" }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell className={classes.narrowCell}>{i18n.t("pendingMessages.table.pendingAt")}</TableCell>
                <TableCell className={classes.narrowCell}>{i18n.t("pendingMessages.table.contact")}</TableCell>
                <TableCell className={classes.narrowCell}>{i18n.t("pendingMessages.table.pendingUser")}</TableCell>
                <TableCell className={classes.narrowCell}>{i18n.t("pendingMessages.table.whatsapp")}</TableCell>
                <TableCell className={classes.statusCell}>{i18n.t("pendingMessages.table.status")}</TableCell>
                <TableCell>{i18n.t("pendingMessages.table.message")}</TableCell>
                <TableCell align="center" className={classes.narrowCell}>{i18n.t("pendingMessages.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={7} />
              ) : (
                messages.map((message) => {
                  const msgId = String(message?.id || "");
                  const isDisabled = removingMessageIds.has(msgId) || updatingStatusIds.has(msgId);
                  return (
                    <TableRow key={message.id}>
                      <TableCell className={classes.narrowCell}>{formatDateTime(message.pendingAt)}</TableCell>
                      <TableCell className={classes.narrowCell}>{message?.contact?.name || "-"}</TableCell>
                      <TableCell className={classes.narrowCell}>
                        {message?.pendingUser?.name || userNameById.get(String(message?.pendingUserId || "")) || "-"}
                      </TableCell>
                      <TableCell className={classes.narrowCell}>{message?.ticket?.whatsapp?.name || "-"}</TableCell>
                      <TableCell className={classes.statusCell}>
                        <StatusChip
                          messageId={message.id}
                          currentStatus={message.pendingStatus}
                          onStatusChange={handleUpdatePendingStatus}
                          disabled={isDisabled}
                        />
                      </TableCell>
                      <TableCell className={classes.messageCell}>{getMessagePreview(message)}</TableCell>
                      <TableCell align="center" className={classes.narrowCell}>
                        <div className={classes.actionsCell}>
                          <Tooltip title={i18n.t("pendingMessages.table.removePending")}>
                            <span>
                              <IconButton
                                size="small"
                                className={classes.deleteButton}
                                disabled={isDisabled}
                                onClick={() => handleRemovePendingMessage(message?.id)}
                                aria-label={i18n.t("pendingMessages.table.removePending")}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={i18n.t("pendingMessages.table.viewMessages")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                className={classes.actionButton}
                                disabled={
                                  isDisabled
                                  || (!message?.ticket?.uuid && !message?.ticket?.id)
                                }
                                onClick={() => handleOpenTicketMessages(message)}
                                aria-label={i18n.t("pendingMessages.table.viewMessages")}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.paginationWrapper}>
          <TablePagination
            component="div"
            count={count}
            page={pageNumber}
            onPageChange={(_event, newPage) => setPageNumber(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage={i18n.t("pendingMessages.rowsPerPage")}
          />
        </div>

        <TicketModal
          open={ticketModalOpen}
          onClose={handleCloseTicketModal}
          ticketId={ticketUuid}
          jumpToMessageId={jumpToMessageId}
          jumpToMessageTs={jumpToMessageTs}
          messageHistoryModeOverride="ticket"
        />
      </Paper>
    </MainContainer>
  );
};

export default PendingMessages;
